/**
 * Pure maths behind the Paseo (walk-through) mode: projecting a room's 360° photo onto
 * the plan's walls, which room a point is in, and a walking route through the doors.
 * walk.ts does the three.js; the projection in its shader mirrors `panoUv` exactly.
 *
 * Frames: plan metres are (x, y) with y = capture-world Z; in three.js the scene uses
 * (x, height, z = plan y), like the Maqueta. A panorama's pixel column is
 * yaw = u·2π − π about +Y with yaw 0 toward capture +X and yaw π/2 toward +Z
 * (tools/pano/reproject.mjs). FloorPlan.panoYaw is capture +X's heading on the plan.
 */
import type { PlanOpening, PlanRoom, Vec2 } from "@pisorama/shared";
import { insidePolygon, wallPieces } from "./modelGeometry.js";

/** Where the phone was during capture, above the floor. The view is exact only here. */
export const EYE_HEIGHT = 1.55;
/** Walls in the walk-through are full height: you're inside the room. */
export const WALK_WALL_HEIGHT = 2.5;
/** Door openings stop here; a lintel fills the rest up to the ceiling. */
export const DOOR_HEIGHT = 2.05;
/** How far past a door's line we probe to find the room on each side (m). */
const DOOR_PROBE = 0.25;

/**
 * Texture coordinate for a direction seen from the capture point, three.js convention
 * (v = 0 at the bottom). `d` is in scene axes (x, up, z = plan y).
 */
export function panoUv(d: { x: number; y: number; z: number }, panoYaw: number): [number, number] {
  const c = Math.cos(-panoYaw);
  const s = Math.sin(-panoYaw);
  const cx = c * d.x - s * d.z;
  const cz = s * d.x + c * d.z;
  const yaw = Math.atan2(cz, cx);
  const pitch = Math.atan2(d.y, Math.hypot(cx, cz));
  return [(yaw + Math.PI) / (2 * Math.PI), 0.5 + pitch / Math.PI];
}

/** Index of the room containing `p`, or -1. */
export function roomAt(p: Vec2, rooms: PlanRoom[]): number {
  return rooms.findIndex((r) => insidePolygon(p, r.outline));
}

export interface DoorLink {
  a: number;
  b: number;
  /** Midpoint of the doorway: routes pass through here. */
  mid: Vec2;
}

/** Which two rooms each door joins (doors onto nowhere, like the front door, are skipped). */
export function doorLinks(rooms: PlanRoom[], openings: PlanOpening[]): DoorLink[] {
  const links: DoorLink[] = [];
  for (const o of openings) {
    if (o.kind !== "door") continue;
    const dx = o.b.x - o.a.x;
    const dy = o.b.y - o.a.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) continue;
    const mid = { x: (o.a.x + o.b.x) / 2, y: (o.a.y + o.b.y) / 2 };
    const n = { x: (-dy / len) * DOOR_PROBE, y: (dx / len) * DOOR_PROBE };
    const a = roomAt({ x: mid.x + n.x, y: mid.y + n.y }, rooms);
    const b = roomAt({ x: mid.x - n.x, y: mid.y - n.y }, rooms);
    if (a >= 0 && b >= 0 && a !== b) links.push({ a, b, mid });
  }
  return links;
}

/**
 * Waypoints from `from` to `to` (both plan metres), through door midpoints, by the
 * fewest doors. Straight to `to` when both are in the same room or either is outside
 * every room; null when the rooms aren't connected by doors.
 */
export function walkPath(from: Vec2, to: Vec2, rooms: PlanRoom[], links: DoorLink[]): Vec2[] | null {
  const start = roomAt(from, rooms);
  const goal = roomAt(to, rooms);
  if (start < 0 || goal < 0 || start === goal) return [to];
  const prev = new Map<number, { room: number; door: Vec2 }>();
  const seen = new Set([start]);
  const queue = [start];
  while (queue.length) {
    const room = queue.shift()!;
    if (room === goal) break;
    for (const l of links) {
      const next = l.a === room ? l.b : l.b === room ? l.a : -1;
      if (next < 0 || seen.has(next)) continue;
      seen.add(next);
      prev.set(next, { room, door: l.mid });
      queue.push(next);
    }
  }
  if (!seen.has(goal)) return null;
  const doors: Vec2[] = [];
  for (let r = goal; r !== start; ) {
    const step = prev.get(r)!;
    doors.unshift(step.door);
    r = step.room;
  }
  return [...doors, to];
}

export interface WallSpan {
  a: Vec2;
  b: Vec2;
  /** A doorway: open up to DOOR_HEIGHT, lintel above. */
  door: boolean;
}

/** One room edge split into solid stretches and doorways. */
export function edgeSpans(a: Vec2, b: Vec2, openings: PlanOpening[]): WallSpan[] {
  const solids = wallPieces([{ a, b }], openings.filter((o) => o.kind === "door"));
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const t = (p: Vec2) => (len === 0 ? 0 : Math.hypot(p.x - a.x, p.y - a.y) / len);
  const at = (u: number): Vec2 => ({ x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u });
  const spans: WallSpan[] = [];
  let cursor = 0;
  for (const piece of solids) {
    const t0 = t(piece.wall.a);
    const t1 = t(piece.wall.b);
    if (t0 - cursor > 1e-3) spans.push({ a: at(cursor), b: at(t0), door: true });
    spans.push({ a: at(t0), b: at(t1), door: false });
    cursor = t1;
  }
  if (1 - cursor > 1e-3) spans.push({ a: at(cursor), b: at(1), door: true });
  return spans;
}

/** Signed area (plan x/y): positive when the outline runs counter-clockwise. */
export function signedArea(outline: Vec2[]): number {
  let s = 0;
  for (let i = 0; i < outline.length; i++) {
    const p = outline[i]!;
    const q = outline[(i + 1) % outline.length]!;
    s += p.x * q.y - q.x * p.y;
  }
  return s / 2;
}

/** True when moving from `p` to `q` crosses a solid wall (doorways let you through). */
export function crossesWall(p: Vec2, q: Vec2, walls: Array<{ a: Vec2; b: Vec2 }>): boolean {
  const cross = (o: Vec2, a: Vec2, b: Vec2) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  return walls.some((w) => {
    const d1 = cross(w.a, w.b, p);
    const d2 = cross(w.a, w.b, q);
    const d3 = cross(p, q, w.a);
    const d4 = cross(p, q, w.b);
    return d1 * d2 < 0 && d3 * d4 < 0;
  });
}

/**
 * The walk-through needs room outlines, a known photo heading, and at least one room
 * with a photo taken inside it; otherwise there is nothing true to show.
 */
export function canWalk(plan: { rooms: PlanRoom[]; panoYaw?: number } | undefined, nodes: Array<{ plan: Vec2 }>): boolean {
  if (!plan || plan.panoYaw === undefined || plan.rooms.length === 0) return false;
  return nodes.some((n) => roomAt(n.plan, plan.rooms) >= 0);
}
